import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { LocaleService } from '@core/services/locale.service';
import { PermissionService } from '@core/services/permission.service';
import { AuthStore } from '@core/store/core/auth.store';
import {
  LucideBookOpen,
  LucideBriefcase,
  LucideBuilding2,
  LucideCar,
  LucideCarFront,
  LucideDatabase,
  LucideGraduationCap,
  LucideLayoutGrid,
  LucideLink,
  LucideMegaphone,
  LucideMessagesSquare,
  LucidePercent,
  LucideStore,
  LucideTriangleAlert,
  LucideTruck,
  LucideUsers,
  LucideUsersRound,
} from '@lucide/angular';
import { MMSCAN_APP_SHELL, type MmscanLogo } from '@mmscan/angular-ui/app-shell';
import { MmscanButton } from '@mmscan/angular-ui/button';
import { MmscanIcon } from '@mmscan/angular-ui/icon';
import type { MmscanNavItem } from '@mmscan/angular-ui/navigation';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { visibleNavItems } from '@shared/utils/nav-item.utils';
import { LucideInstagramBrand, LucideLinkedinBrand } from './brand-icons';
import { HeaderActionsComponent } from './header-actions/header-actions.component';
import { NotificationComponent } from './notification/notification.component';
import { ShellSidebarService } from './shell-sidebar.service';
import { VEHICLE_CATALOG_RESOURCES } from '@features/vehicle-catalog/vehicle-catalog.sections';

/**
 * Every resource the main navigation gates an item on. `authStore.loadFeaturesByResources` fetches
 * exactly this list, so a resource missing here leaves its item hidden with nothing logged: add it
 * in the same change as the item.
 */
const NAV_RESOURCES = [
  'micare',
  'access_control',
  'message_board_post',
  'recall',
  ...VEHICLE_CATALOG_RESOURCES,
  'vehicle_offer',
  'imd_program',
] as const;

@Component({
  selector: 'mp-layout',
  imports: [
    ...MMSCAN_APP_SHELL,
    RouterOutlet,
    TranslatePipe,
    MmscanButton,
    MmscanIcon,
    HeaderActionsComponent,
    NotificationComponent,
  ],
  templateUrl: './layout.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex h-svh flex-col' },
})
export class LayoutComponent {
  private readonly authStore = inject(AuthStore);
  private readonly permissions = inject(PermissionService);
  private readonly translate = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly sidebar = inject(ShellSidebarService);

  protected readonly linkedin = LucideLinkedinBrand;
  protected readonly instagram = LucideInstagramBrand;
  protected readonly currentYear = new Date().getFullYear();

  protected readonly section = this.sidebar.section;

  protected readonly logo = computed<MmscanLogo>(() => {
    this.localeService.locale();
    return {
      src: 'logos/logo-miportal-black.svg',
      darkSrc: 'logos/logo-miportal-white.svg',
      alt: this.translate.instant('common.altText.mitsubishiLogo'),
      route: '/home',
      width: 76,
      height: 14,
    };
  });

  protected readonly footerLogo = computed<MmscanLogo>(() => {
    this.localeService.locale();
    return {
      src: 'logos/logo-horizontal-black.svg',
      darkSrc: 'logos/logo-horizontal-white.svg',
      alt: this.translate.instant('common.altText.mitsubishiLogo'),
      width: 124,
      height: 12,
    };
  });

  /** Rebuilt when the gated resources arrive and when the language switches (instant() reads it). */
  protected readonly items = computed<MmscanNavItem[]>(() => {
    this.authStore.featuresByResource();
    this.localeService.locale();
    const t = (key: string) => this.translate.instant(key);
    const canList = (resource: string) => this.permissions.canList(resource);

    return visibleNavItems([
      {
        label: t('nav.menu.adminTools'),
        icon: LucideDatabase,
        children: [
          {
            label: t('nav.menu.accessControl'),
            icon: LucideUsers,
            route: '/access-control',
            canAccess: canList('access_control'),
          },
          {
            label: t('nav.menu.messageBoard'),
            icon: LucideMessagesSquare,
            route: '/message-board',
            canAccess: canList('message_board_post'),
          },
        ],
      },
      {
        label: t('nav.menu.internalTools'),
        icon: LucideLayoutGrid,
        children: [
          { label: 'MiCare', icon: LucideBookOpen, route: '/micare', canAccess: canList('micare') },
          { label: 'MiRide', icon: LucideCarFront, route: '/miride' },
          { label: t('nav.menu.recalls'), icon: LucideTriangleAlert, route: '/recalls', canAccess: canList('recall') },
          // Shown to anyone who can list one of its pages; the section lands on the first of them.
          {
            label: t('nav.menu.vehicleCatalog'),
            icon: LucideCar,
            route: '/vehicle-catalog',
            canAccess: this.permissions.canListAny([...VEHICLE_CATALOG_RESOURCES]),
          },
          {
            label: t('nav.menu.offers'),
            icon: LucideMegaphone,
            route: '/offers',
            canAccess: canList('vehicle_offer'),
          },
          {
            label: t('nav.menu.imdPrograms'),
            icon: LucidePercent,
            route: '/imd-programs',
            canAccess: canList('imd_program'),
          },
        ],
      },
      {
        label: t('nav.menu.dealerTools'),
        icon: LucideBriefcase,
        children: [{ label: t('nav.menu.miTechForum'), icon: LucideUsersRound, disabled: true }],
      },
      {
        label: t('nav.menu.externalLinks'),
        icon: LucideLink,
        children: [
          { label: 'Mitsubishi Dealer Link', icon: LucideBuilding2, url: 'https://mdl.mmsa.com/Extranet/index.htm' },
          { label: 'GoFarAcademy', icon: LucideGraduationCap, url: 'https://www.gofaracademy.ca/account/awslogin' },
          { label: 'MiShowroom', icon: LucideStore, url: 'https://www.miadvantage.ca/mishowroom/redirect' },
          { label: 'MiServiceView', icon: LucideCar, url: 'https://serviceview-mmscan.na.urbansciencesolutions.com' },
          { label: t('nav.menu.midelivery'), icon: LucideTruck, url: 'https://www.miadvantage.ca/redirect' },
        ],
      },
    ]);
  });

  constructor() {
    effect(() => {
      if (this.authStore.isLoggedIn()) void this.authStore.loadFeaturesByResources(NAV_RESOURCES);
    });
  }
}
